import { STYLES, injectKeyframes } from './styles';

const MODAL_ID = 'nbx-hub-modal';
const OVERLAY_ID = 'nbx-hub-overlay';

let isFullscreen = false;

/**
 * Creates the dark overlay backdrop
 */
export function createOverlay(onClick: () => void): HTMLDivElement {
  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.setAttribute('style', STYLES.overlay);
  overlay.setAttribute('data-nbx-overlay', 'true');

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      onClick();
    }
  });

  return overlay;
}

/**
 * Creates the white modal container
 */
export function createModalContainer(): HTMLDivElement {
  const modal = document.createElement('div');
  modal.id = MODAL_ID;
  modal.setAttribute('style', STYLES.modal);
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'NBX Hub');

  return modal;
}

/**
 * Creates the X close button
 */
export function createCloseButton(onClick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.setAttribute('style', STYLES.closeButton);
  button.setAttribute('aria-label', 'Cerrar');
  button.setAttribute('type', 'button');
  button.innerHTML = '&#x2715;'; // × symbol

  button.addEventListener('click', onClick);

  button.addEventListener('mouseenter', () => {
    button.setAttribute('style', STYLES.closeButton + STYLES.closeButtonHover);
  });

  button.addEventListener('mouseleave', () => {
    button.setAttribute('style', STYLES.closeButton);
  });

  return button;
}

/**
 * Creates the iframe element
 */
export function createIframe(src: string): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('style', STYLES.iframe);
  iframe.setAttribute('src', src);
  iframe.setAttribute('allow', 'clipboard-write');
  iframe.setAttribute('title', 'NBX Hub');

  return iframe;
}

/**
 * Creates loading indicator
 */
export function createLoader(): HTMLDivElement {
  injectKeyframes();

  const loader = document.createElement('div');
  loader.setAttribute('style', STYLES.loader);
  loader.setAttribute('data-nbx-loader', 'true');

  const spinner = document.createElement('div');
  spinner.setAttribute('style', STYLES.spinner);

  const text = document.createElement('span');
  text.setAttribute('style', STYLES.loaderText);
  text.textContent = 'Cargando...';

  loader.appendChild(spinner);
  loader.appendChild(text);

  return loader;
}

/**
 * Shows the modal with animation
 */
export function showModal(overlay: HTMLDivElement, modal: HTMLDivElement): void {
  document.body.appendChild(overlay);
  overlay.appendChild(modal);

  // Force reflow for animation
  overlay.offsetHeight;

  overlay.setAttribute('style', STYLES.overlay + STYLES.overlayVisible);
  modal.setAttribute('style', STYLES.modal + STYLES.modalVisible);
}

/**
 * Hides the modal with animation and removes from DOM
 */
export function hideModal(overlay: HTMLDivElement, modal: HTMLDivElement): Promise<void> {
  return new Promise((resolve) => {
    overlay.setAttribute('style', STYLES.overlay);
    modal.setAttribute('style', STYLES.modal);

    setTimeout(() => {
      overlay.remove();
      resolve();
    }, 200);
  });
}

/**
 * Removes loader from modal
 */
export function removeLoader(modal: HTMLDivElement): void {
  const loader = modal.querySelector('[data-nbx-loader]');
  if (loader) {
    loader.remove();
  }
}

/**
 * Checks if modal is currently in the DOM
 */
export function isModalOpen(): boolean {
  return document.getElementById(OVERLAY_ID) !== null;
}

/**
 * Gets the current modal elements if they exist
 */
export function getModalElements(): { overlay: HTMLDivElement; modal: HTMLDivElement } | null {
  const overlay = document.getElementById(OVERLAY_ID) as HTMLDivElement | null;
  const modal = document.getElementById(MODAL_ID) as HTMLDivElement | null;

  if (overlay && modal) {
    return { overlay, modal };
  }

  return null;
}

/**
 * Sets the modal to fullscreen or back to normal size
 */
export function setModalFullscreen(fullscreen: boolean): void {
  const elements = getModalElements();
  if (!elements) return;

  const { modal } = elements;

  if (fullscreen === isFullscreen) return;

  isFullscreen = fullscreen;

  if (fullscreen) {
    modal.setAttribute('style', STYLES.modal + STYLES.modalVisible + STYLES.modalFullscreen);
  } else {
    modal.setAttribute('style', STYLES.modal + STYLES.modalVisible);
  }
}

/**
 * Returns whether the modal is currently in fullscreen mode
 */
export function isModalFullscreen(): boolean {
  return isFullscreen;
}

/**
 * Resets fullscreen state (called when modal closes)
 */
export function resetFullscreenState(): void {
  isFullscreen = false;
}
